import React, { useEffect, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatCurrency, WEEKDAY_NAMES } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Calendar, Tag } from 'lucide-react';
import { LoadingBlock, EmptyState } from '../../components/common/States';
import { useAuth } from '../../App';

const DEFAULT_FORM = {
  rule_type: 'weekday',
  weekday: null,
  specific_date: '',
  event_title: '',
  description: '',
  amount: 0,
  amount_type: 'flat',
  is_active: true,
  priority: 0,
};

export default function PricingRulesPage() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const canEdit = ['super_admin', 'manager'].includes(user?.role);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getPricingRules();
      setRules(res.data.rules);
    } catch { toast.error('Failed to load pricing rules'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditingRule(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      rule_type: rule.rule_type,
      weekday: rule.weekday,
      specific_date: rule.specific_date || '',
      event_title: rule.event_title || '',
      description: rule.description || '',
      amount: rule.amount,
      amount_type: rule.amount_type,
      is_active: rule.is_active,
      priority: rule.priority,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (form.amount < 0) { toast.error('Amount cannot be negative'); return; }
    if (form.rule_type === 'weekday' && form.weekday === null) { toast.error('Select a weekday'); return; }
    if (form.rule_type === 'specific_date' && !form.specific_date) { toast.error('Enter a specific date'); return; }

    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), priority: parseInt(form.priority) || 0 };
      if (form.rule_type === 'weekday') payload.specific_date = null;
      else payload.weekday = null;

      if (editingRule) {
        await adminApi.updatePricingRule(editingRule._id, payload);
        toast.success('Rule updated');
      } else {
        await adminApi.createPricingRule(payload);
        toast.success('Rule created');
      }
      setDialogOpen(false);
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    try {
      await adminApi.deletePricingRule(id);
      toast.success('Rule deleted');
      fetch();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (rule) => {
    try {
      await adminApi.updatePricingRule(rule._id, { is_active: !rule.is_active });
      setRules(rs => rs.map(r => r._id === rule._id ? { ...r, is_active: !r.is_active } : r));
      toast.success(rule.is_active ? 'Rule deactivated' : 'Rule activated');
    } catch { toast.error('Failed to update rule'); }
  };

  return (
    <AdminShell
      title="Pricing Rules"
      actions={
        canEdit ? (
          <Button
            size="sm"
            className="btn-gold gap-1.5 text-xs h-8"
            onClick={openCreate}
            data-testid="pricing-rule-create-button"
          >
            <Plus size={13} /> New Rule
          </Button>
        ) : null
      }
    >
      <div className="space-y-4">
        {loading ? (
          <LoadingBlock rows={5} />
        ) : rules.length === 0 ? (
          <EmptyState
            title="No pricing rules yet"
            description="Create your first rule to start charging cover fees."
            action={canEdit ? openCreate : null}
            actionLabel="Create Rule"
          />
        ) : (
          <div className="space-y-3">
            {/* Weekday Rules */}
            <div>
              <p className="text-xs text-[#D4A843] uppercase tracking-wider font-semibold mb-2">Weekday Rules</p>
              {rules.filter(r => r.rule_type === 'weekday').map(rule => (
                <div key={rule._id} className="surface-card card-highlight rounded-xl p-4 mb-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${rule.is_active ? 'bg-[#2FBF71]' : 'bg-[#6F6F6F]'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#F2F2F2] font-medium text-sm">
                          {rule.weekday !== null && rule.weekday !== undefined ? WEEKDAY_NAMES[rule.weekday] : '—'}
                        </span>
                        {rule.event_title && (
                          <span className="text-xs text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded-full">{rule.event_title}</span>
                        )}
                      </div>
                      <p className="text-xs text-[#B9B9B9]">
                        {formatCurrency(rule.amount)} {rule.amount_type === 'per_guest' ? '/ guest' : 'flat'}
                        {rule.priority > 0 && <span className="ml-2 text-[#6F6F6F]">Priority: {rule.priority}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={rule.is_active} onCheckedChange={() => handleToggle(rule)} disabled={!canEdit} />
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#B9B9B9] hover:text-[#F2F2F2]" onClick={() => openEdit(rule)}>
                          <Edit size={13} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E05252] hover:bg-[#2A1414]" onClick={() => handleDelete(rule._id)}>
                          <Trash2 size={13} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {rules.filter(r => r.rule_type === 'weekday').length === 0 && (
                <p className="text-[#6F6F6F] text-sm px-1">No weekday rules yet.</p>
              )}
            </div>

            {/* Specific Date Rules */}
            <div className="mt-4">
              <p className="text-xs text-[#D4A843] uppercase tracking-wider font-semibold mb-2">Special Date Rules</p>
              {rules.filter(r => r.rule_type === 'specific_date').map(rule => (
                <div key={rule._id} className="surface-card card-highlight rounded-xl p-4 mb-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${rule.is_active ? 'bg-[#2FBF71]' : 'bg-[#6F6F6F]'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#F2F2F2] font-medium text-sm">{rule.specific_date}</span>
                        {rule.event_title && (
                          <span className="text-xs text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded-full">{rule.event_title}</span>
                        )}
                      </div>
                      <p className="text-xs text-[#B9B9B9]">
                        {formatCurrency(rule.amount)} {rule.amount_type === 'per_guest' ? '/ guest' : 'flat'}
                        {rule.priority > 0 && <span className="ml-2 text-[#6F6F6F]">Priority: {rule.priority}</span>}
                      </p>
                      {rule.description && <p className="text-xs text-[#6F6F6F] mt-0.5">{rule.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={rule.is_active} onCheckedChange={() => handleToggle(rule)} disabled={!canEdit} />
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#B9B9B9]" onClick={() => openEdit(rule)}><Edit size={13} /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-[#E05252] hover:bg-[#2A1414]" onClick={() => handleDelete(rule._id)}><Trash2 size={13} /></Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {rules.filter(r => r.rule_type === 'specific_date').length === 0 && (
                <p className="text-[#6F6F6F] text-sm px-1">No special date rules yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-['EB_Garamond'] text-xl text-[#F2F2F2]">
              {editingRule ? 'Edit Pricing Rule' : 'New Pricing Rule'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Rule Type */}
            <div className="grid grid-cols-2 gap-2">
              {['weekday', 'specific_date'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, rule_type: t }))}
                  className={`p-2.5 rounded-lg border text-sm font-medium transition-colors ${
                    form.rule_type === t
                      ? 'border-[#D4A843]/60 bg-[#D4A843]/10 text-[#D4A843]'
                      : 'border-[#2A2A2A] bg-[#111111] text-[#B9B9B9] hover:border-[#D4A843]/30'
                  }`}
                >
                  {t === 'weekday' ? 'Weekday Rule' : 'Specific Date'}
                </button>
              ))}
            </div>

            {form.rule_type === 'weekday' ? (
              <div className="space-y-1">
                <Label className="text-[#B9B9B9] text-xs">Weekday</Label>
                <Select
                  value={form.weekday !== null && form.weekday !== undefined ? String(form.weekday) : ''}
                  onValueChange={v => setForm(f => ({ ...f, weekday: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-[#111111] border-[#2A2A2A]">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                    {WEEKDAY_NAMES.map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1">
                <Label className="text-[#B9B9B9] text-xs">Specific Date</Label>
                <Input
                  type="date"
                  value={form.specific_date}
                  onChange={e => setForm(f => ({ ...f, specific_date: e.target.value }))}
                  className="bg-[#111111] border-[#2A2A2A]"
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">Event Title (optional)</Label>
              <Input value={form.event_title} onChange={e => setForm(f => ({...f, event_title: e.target.value}))} placeholder="e.g. Ladies Night" className="bg-[#111111] border-[#2A2A2A]" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#B9B9B9] text-xs">Amount (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.amount}
                  onChange={e => setForm(f => ({...f, amount: e.target.value}))}
                  className="bg-[#111111] border-[#2A2A2A]"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[#B9B9B9] text-xs">Amount Type</Label>
                <Select value={form.amount_type} onValueChange={v => setForm(f => ({...f, amount_type: v}))}>
                  <SelectTrigger className="bg-[#111111] border-[#2A2A2A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                    <SelectItem value="flat">Flat per booking</SelectItem>
                    <SelectItem value="per_guest">Per guest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-[#B9B9B9] text-xs">Priority (higher = preferred)</Label>
              <Input
                type="number"
                min={0}
                value={form.priority}
                onChange={e => setForm(f => ({...f, priority: e.target.value}))}
                className="bg-[#111111] border-[#2A2A2A]"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-[#B9B9B9] text-xs">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#B9B9B9]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-gold">
              {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
